import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ClipboardList,
  Clock,
  DollarSign,
  Droplet,
  FileBarChart,
  Copy,
  History,
  Pencil,
  Plus,
  Trash2,
  Truck,
  Users,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import { useAppAccess } from "@/hooks/useAppAccess";
import type {
  AbastecimentoDto,
  ContratoDto,
  ContratoOperadorDto,
  LocacoesSnapshot,
  MaquinarioDto,
  ManutencaoDto,
  ManutencaoStatus,
  RegistroHorimetroDto,
} from "@/services/locacoes";
import {
  CONTRATO_STATUS_LABELS,
  extractApiError,
  fetchContratoFaturamento,
  fetchLeadTimeValorCarrada,
  isContratoPorCarradas,
  isContratoPorHoras,
  isCombustivelPagoCliente,
  isContratoEquipamento,
  isContratoModeloMensalEquipamento,
  sortVigenciasValorCarradaDesc,
  vigenciaValorEstaAberta,
  type ContratoFaturamentoDto,
  type LeadTimeValorCarradaFaixaDto,
  MANUTENCAO_PRIORIDADE_LABELS,
  MANUTENCAO_STATUS_LABELS,
  MANUTENCAO_TIPO_LABELS,
  MAQUINARIO_STATUS_LABELS,
} from "@/services/locacoes";
import {
  fetchCarradasLancamentos,
  fetchCaminhoesCarradas,
  groupCarradasEmViagens,
  quantidadeFaturavelCarradaDto,
  type CaminhaoCarradasDto,
  type CarradaDto,
  type ContratoCarradasDto,
} from "@/services/carradas";
import { formatDate, formatDateTime, formatNumber } from "@/utils/format";
import { TabHorimetro } from "./TabHorimetro";
import { TabApropriacaoCarradas } from "./TabApropriacaoCarradas";
import { TabAbastecimentos } from "./TabAbastecimentos";
import { TabRelatorios } from "./TabRelatorios";
import { TabOperadores } from "./TabOperadores";
import { ModalReajusteValorCarrada } from "./ModalReajusteValorCarrada";
import { ModalLeadTimeValorCarrada } from "./ModalLeadTimeValorCarrada";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import {
  buildReportPeriodForContrato,
  filterAbastecimentosDoContrato,
  filterMaquinariosByIds,
  filterManutencoesDoContrato,
  filterRegistrosDoContrato,
  formatContratoValor,
  getHorimetroContratoMaquina,
  locacoesPanelClass,
  locacoesTabClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
  periodForContrato,
  resolveMaquinaNome,
  snapshotDataVersion,
} from "./locacoesUtils";
import { LocacoesStatCard } from "./LocacoesStatCard";

type DetailTab = "horimetro" | "abastecimentos" | "maquinarios" | "manutencoes" | "relatorios" | "operadores";

const DETAIL_TABS: Array<{ id: DetailTab; label: string; icon: typeof Clock }> = [
  { id: "horimetro", label: "Horímetros", icon: Clock },
  { id: "abastecimentos", label: "Abastecimentos", icon: Droplet },
  { id: "maquinarios", label: "Maquinários", icon: Truck },
  { id: "manutencoes", label: "Manutenções", icon: Wrench },
  { id: "relatorios", label: "Relatórios", icon: FileBarChart },
  { id: "operadores", label: "Operadores", icon: Users },
];

export interface ContratoDetailViewProps {
  contrato: ContratoDto;
  snapshot: LocacoesSnapshot;
  busy?: boolean;
  onBack: () => void;
  onEditContrato: () => void;
  onSaveOperadores: (operadores: ContratoOperadorDto[]) => Promise<void>;
  onNovoHorimetro: () => void;
  onEditHorimetro: (r: RegistroHorimetroDto) => void;
  onDeleteHorimetro?: (r: RegistroHorimetroDto) => void;
  onNovoAbastecimento?: () => void;
  onEditAbastecimento?: (a: AbastecimentoDto) => void;
  onDeleteAbastecimento?: (a: AbastecimentoDto) => void;
  onEditMaquina?: (m: MaquinarioDto) => void;
  onDuplicateMaquina?: (m: MaquinarioDto) => void;
  onNovaMaquina?: () => void;
  onHistoricoMaquina?: (m: MaquinarioDto) => void;
  onNovaManutencao?: () => void;
  onEditManutencao?: (m: ManutencaoDto) => void;
  onDeleteManutencao?: (m: ManutencaoDto) => void;
  onUpdateManutencaoStatus: (id: string, status: ManutencaoStatus) => void;
  /** Recarrega snapshot (ex.: após reajuste de valor no contrato). */
  onRefreshSnapshot?: () => void | Promise<void>;
  operadorMode?: boolean;
  operadoresCatalog?: import("@/services/locacoes").OperadorCatalogDto[];
}

export function ContratoDetailView({
  contrato,
  snapshot,
  busy,
  onBack,
  onEditContrato,
  onSaveOperadores,
  onNovoHorimetro,
  onEditHorimetro,
  onDeleteHorimetro,
  onNovoAbastecimento,
  onEditAbastecimento,
  onDeleteAbastecimento,
  onEditMaquina,
  onDuplicateMaquina,
  onNovaMaquina,
  onHistoricoMaquina,
  onNovaManutencao,
  onEditManutencao,
  onDeleteManutencao,
  onUpdateManutencaoStatus,
  onRefreshSnapshot,
  operadorMode = false,
  operadoresCatalog = [],
}: ContratoDetailViewProps) {
  const { isAdmin } = useAppAccess();
  const porCarradas = isContratoPorCarradas(contrato.modeloContrato);
  const [detailTab, setDetailTab] = useState<DetailTab>("horimetro");
  const [faturamento, setFaturamento] = useState<ContratoFaturamentoDto | null>(null);
  const [faturamentoError, setFaturamentoError] = useState<string | null>(null);
  const [carradasLancamentos, setCarradasLancamentos] = useState<CarradaDto[]>([]);
  const [caminhoesCarradas, setCaminhoesCarradas] = useState<CaminhaoCarradasDto[]>([]);
  const [reajusteOpen, setReajusteOpen] = useState(false);
  const [leadTimeOpen, setLeadTimeOpen] = useState(false);
  const [leadTimeFaixas, setLeadTimeFaixas] = useState<LeadTimeValorCarradaFaixaDto[]>([]);
  const dataVersion = snapshotDataVersion(snapshot);

  const maquinas = useMemo(
    () => filterMaquinariosByIds(snapshot.maquinarios, contrato.maquinaIds),
    [snapshot.maquinarios, contrato.maquinaIds],
  );
  const registros = useMemo(
    () => filterRegistrosDoContrato(contrato, snapshot.registrosHorimetro ?? []),
    [snapshot.registrosHorimetro, contrato],
  );
  const abastecimentos = useMemo(
    () => filterAbastecimentosDoContrato(contrato, snapshot.abastecimentos ?? []),
    [snapshot.abastecimentos, contrato],
  );
  const manutencoes = useMemo(
    () => filterManutencoesDoContrato(contrato, snapshot.manutencoes ?? []),
    [snapshot.manutencoes, contrato],
  );

  const loadCarradasContrato = useCallback(async () => {
    if (!porCarradas) {
      setCarradasLancamentos([]);
      setCaminhoesCarradas([]);
      setLeadTimeFaixas([]);
      return;
    }
    const [lancamentos, caminhoes, lead] = await Promise.all([
      fetchCarradasLancamentos({ contratoId: contrato.id }),
      fetchCaminhoesCarradas(),
      fetchLeadTimeValorCarrada(contrato.id).catch(() => ({ contratoId: contrato.id, faixas: [] })),
    ]);
    setCarradasLancamentos(lancamentos);
    setCaminhoesCarradas(caminhoes);
    setLeadTimeFaixas(lead.faixas);
  }, [porCarradas, contrato.id]);

  const refreshAposCarrada = useCallback(async () => {
    await loadCarradasContrato();
    await onRefreshSnapshot?.();
  }, [loadCarradasContrato, onRefreshSnapshot]);

  useEffect(() => {
    void loadCarradasContrato().catch(() => {
      setCarradasLancamentos([]);
      setCaminhoesCarradas([]);
    });
  }, [loadCarradasContrato, dataVersion]);

  const totalCarradasRegistradas = useMemo(
    () =>
      carradasLancamentos.reduce(
        (s, c) => s + quantidadeFaturavelCarradaDto(c, carradasLancamentos),
        0,
      ),
    [carradasLancamentos],
  );
  const viagensRegistradas = useMemo(
    () => groupCarradasEmViagens(carradasLancamentos).length,
    [carradasLancamentos],
  );

  const contratoCarradasDto = useMemo<ContratoCarradasDto>(
    () => ({
      id: contrato.id,
      codigoContrato: contrato.codigoContrato,
      clienteSiengeNome: contrato.clienteSiengeNome,
      maquinaIds: [...contrato.maquinaIds],
      valorCarradaVigencias: [...(contrato.valorCarradaVigencias ?? [])],
      dataInicio: contrato.dataInicio,
      dataFimPrevista: contrato.dataFimPrevista,
    }),
    [contrato],
  );

  const period = useMemo(
    () =>
      periodForContrato(contrato, [
        ...registros.map((registro) => registro.dataHora),
        ...abastecimentos.map((abastecimento) => abastecimento.data),
        ...carradasLancamentos.map((c) => c.data),
      ]),
    [contrato, registros, abastecimentos, carradasLancamentos],
  );

  useEffect(() => {
    let cancelled = false;
    setFaturamentoError(null);
    void fetchContratoFaturamento(contrato.id, period)
      .then((data) => {
        if (!cancelled) {
          setFaturamento(data.faturamento);
          setFaturamentoError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setFaturamento(null);
          setFaturamentoError(extractApiError(e, "Não foi possível carregar o faturamento do contrato."));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contrato.id, period.startDate, period.endDate, dataVersion]);

  const manutencoesAbertas = useMemo(
    () => manutencoes.filter((m) => m.status !== "finalizada" && m.statusAprovacao !== "rejeitado").length,
    [manutencoes],
  );

  const detailTabs = useMemo(() => {
    if (operadorMode) {
      return DETAIL_TABS.filter((t) => t.id === "horimetro").map((t) =>
        porCarradas ? { ...t, label: "Apropriação", icon: ClipboardList } : t,
      );
    }
    if (isContratoEquipamento(contrato)) {
      return DETAIL_TABS.filter(
        (t) => t.id !== "horimetro" && t.id !== "abastecimentos" && t.id !== "operadores",
      ).map((t) =>
        t.id === "maquinarios" ? { ...t, label: "Equipamentos" } : t,
      );
    }
    let tabs = DETAIL_TABS;
    if (isCombustivelPagoCliente(contrato) || porCarradas) {
      tabs = tabs.filter((t) => t.id !== "abastecimentos");
    }
    if (porCarradas) {
      tabs = tabs
        .filter((t) => t.id !== "operadores")
        .map((t) =>
          t.id === "horimetro"
            ? { ...t, label: "Apropriação", icon: ClipboardList }
            : t.id === "maquinarios"
              ? { ...t, label: "Caminhões" }
              : t,
        );
    }
    return tabs;
  }, [operadorMode, contrato, porCarradas]);

  useEffect(() => {
    if (isContratoEquipamento(contrato) && ["horimetro", "abastecimentos", "operadores"].includes(detailTab)) {
      setDetailTab("maquinarios");
    }
    if ((isCombustivelPagoCliente(contrato) || porCarradas) && detailTab === "abastecimentos") {
      setDetailTab("horimetro");
    }
    if (porCarradas && detailTab === "operadores") {
      setDetailTab("horimetro");
    }
  }, [contrato, detailTab, porCarradas]);

  return (
    <div className={locacoesTabContentClass}>
      <div className={cn(locacoesPanelClass, "border-l-4 border-l-brand-400")}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Button type="button" variant="ghost" size="sm" onClick={onBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Voltar aos contratos
            </Button>
            <h2 className="mt-2 text-lg font-bold text-slate-900">
              {contrato.codigoContrato} — {contrato.modeloContrato}
            </h2>
            <p className="text-sm text-slate-600">
              {contrato.clienteSiengeNome}
              {contrato.clienteSiengeDocument ? ` • ${contrato.clienteSiengeDocument}` : ""}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {formatContratoValor(contrato)} • {maquinas.length}{" "}
              {isContratoEquipamento(contrato) ? "equipamento(s)" : "máquina(s)"} •{" "}
              {CONTRATO_STATUS_LABELS[contrato.status]} • Início {formatDate(contrato.dataInicio)}
            </p>
            {porCarradas && (contrato.valorCarradaVigencias?.length ?? 0) > 0 && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Histórico de valor por carrada
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      leftIcon={<Clock className="h-3.5 w-3.5" />}
                      onClick={() => setLeadTimeOpen(true)}
                    >
                      Lead time
                    </Button>
                    {!operadorMode &&
                      contrato.status !== "encerrado" &&
                      contrato.status !== "cancelado" && (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          leftIcon={<DollarSign className="h-3.5 w-3.5" />}
                          onClick={() => setReajusteOpen(true)}
                        >
                          Registrar reajuste
                        </Button>
                      )}
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {(leadTimeFaixas.length > 0
                    ? leadTimeFaixas
                    : sortVigenciasValorCarradaDesc(contrato.valorCarradaVigencias ?? []).map((vigencia) => ({
                        vigencia,
                        totalLancamentos: 0,
                        quantidadeCarradas: 0,
                        valorTotal: 0,
                        carradas: [],
                      }))
                  ).map((faixa) => {
                    const vig = faixa.vigencia;
                    const periodo = vigenciaValorEstaAberta(vig)
                      ? `${formatDateTime(vig.vigenteDesde)} → agora`
                      : `${formatDateTime(vig.vigenteDesde)} → ${formatDateTime(vig.vigenteAte || "")}`;
                    return (
                      <li
                        key={`hist-sum-${vig.id || vig.vigenteDesde}`}
                        className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2.5 text-sm"
                      >
                        <p className="font-medium text-slate-800">
                          {faixa.quantidadeCarradas} carrada(s) · R${" "}
                          {formatNumber(vig.valorUnitario, 2)}
                          {vigenciaValorEstaAberta(vig) ? (
                            <span className="ml-2 text-xs font-semibold text-emerald-700">Vigente</span>
                          ) : null}
                        </p>
                        <p className="text-xs text-slate-500">no período {periodo}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {porCarradas &&
              (contrato.valorCarradaVigencias?.length ?? 0) === 0 &&
              !operadorMode &&
              contrato.status !== "encerrado" &&
              contrato.status !== "cancelado" && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-sm text-amber-900">
                    Contrato sem valor unitário cadastrado. Cadastre o valor inicial na edição do contrato ou
                    registre um reajuste.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    leftIcon={<DollarSign className="h-3.5 w-3.5" />}
                    onClick={() => setReajusteOpen(true)}
                  >
                    Registrar reajuste
                  </Button>
                </div>
              )}
            {contrato.infoBasicos && (
              <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{contrato.infoBasicos}</p>
            )}
            {isCombustivelPagoCliente(contrato) && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Combustível pago pelo cliente — sem registro de abastecimentos Semog; horímetros não entram no consumo
                (L/h).
              </p>
            )}
            {faturamentoError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {faturamentoError}
              </div>
            )}
            {faturamento && !faturamentoError && (
              <div className="mt-3 flex flex-wrap gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <DollarSign className="h-4 w-4" />
                  {isContratoEquipamento(contrato)
                    ? isContratoModeloMensalEquipamento(contrato.modeloContrato)
                      ? `Faturamento no período: R$ ${formatNumber(faturamento.valorPeriodo, 2)} (mensalidade R$ ${formatNumber(contrato.valorMensal ?? 0, 2)})`
                      : `Valor do contrato: R$ ${formatNumber(faturamento.valorPeriodo, 2)}`
                    : isContratoPorCarradas(contrato.modeloContrato)
                      ? `Faturamento no período: R$ ${formatNumber(faturamento.valorPeriodo, 2)} (${formatNumber(faturamento.quantidadeCarradas ?? 0, 0)} carradas • ${formatContratoValor(contrato)})`
                    : isContratoPorHoras(contrato.modeloContrato)
                    ? `Faturamento no período: R$ ${formatNumber(faturamento.valorPeriodo, 2)} (${formatNumber(faturamento.horasTrabalhadas, 1)} h • ${formatContratoValor(contrato)})`
                    : `Valor do contrato: R$ ${formatNumber(faturamento.valorPeriodo, 2)} • ${formatNumber(faturamento.horasTrabalhadas, 1)} h no período`}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && !operadorMode && (
              <Button variant="secondary" size="sm" onClick={onEditContrato} leftIcon={<Pencil className="h-3.5 w-3.5" />}>
                Editar contrato
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
        <LocacoesStatCard
          title={isContratoEquipamento(contrato) ? "Equipamentos" : "Máquinas"}
          value={maquinas.length}
          icon={<Truck className="h-5 w-5" />}
          accentClass="h-full min-w-0 border-l-4 border-l-blue-400"
        />
        {porCarradas ? (
          <LocacoesStatCard
            title="Registro de carradas"
            value={totalCarradasRegistradas}
            subtitle={`${viagensRegistradas} viagem(ns) · ${carradasLancamentos.length} lançamento(s)`}
            icon={<Truck className="h-5 w-5" />}
            accentClass="h-full min-w-0 border-l-4 border-l-emerald-400"
          />
        ) : (
          <LocacoesStatCard
            title="Registros horímetro"
            value={registros.length}
            icon={<Clock className="h-5 w-5" />}
            accentClass="h-full min-w-0 border-l-4 border-l-emerald-400"
          />
        )}
        {!isContratoEquipamento(contrato) && !isCombustivelPagoCliente(contrato) && !porCarradas && (
          <LocacoesStatCard
            title="Abastecimentos"
            value={abastecimentos.length}
            icon={<Droplet className="h-5 w-5" />}
            accentClass="h-full min-w-0 border-l-4 border-l-sky-400"
          />
        )}
        <LocacoesStatCard
          title="Manutenções"
          value={manutencoes.length}
          subtitle={manutencoesAbertas > 0 ? `${manutencoesAbertas} em aberto` : undefined}
          icon={<Wrench className="h-5 w-5" />}
          accentClass="h-full min-w-0 border-l-4 border-l-amber-400"
        />
        {faturamento && !faturamentoError && (
          <LocacoesStatCard
            title="Faturamento período"
            value={`R$ ${formatNumber(faturamento.valorPeriodo, 2)}`}
            icon={<DollarSign className="h-5 w-5" />}
            accentClass="h-full min-w-0 border-l-4 border-l-purple-400"
          />
        )}
      </div>

      <div className="no-print flex flex-wrap gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-card">
        {detailTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setDetailTab(id)}
            className={locacoesTabClass(detailTab === id)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {detailTab === "horimetro" && porCarradas && (
        <TabApropriacaoCarradas
          contrato={contratoCarradasDto}
          caminhoes={caminhoesCarradas}
          lancamentos={carradasLancamentos}
          period={period}
          busy={busy}
          onRefresh={refreshAposCarrada}
        />
      )}

      {detailTab === "horimetro" && !porCarradas && (
        <TabHorimetro
          registros={registros}
          maquinarios={maquinas}
          period={period}
          onNovo={onNovoHorimetro}
          onEdit={onEditHorimetro}
          onDelete={onDeleteHorimetro}
        />
      )}

      {detailTab === "abastecimentos" && onNovoAbastecimento && onEditAbastecimento && onDeleteAbastecimento && (
        <TabAbastecimentos
          abastecimentos={abastecimentos}
          maquinarios={maquinas}
          registrosHorimetro={registros}
          contratos={[contrato]}
          combustivelPagoCliente={isCombustivelPagoCliente(contrato)}
          period={period}
          onNovo={onNovoAbastecimento}
          onEdit={onEditAbastecimento}
          onDelete={onDeleteAbastecimento}
        />
      )}

      {detailTab === "maquinarios" && onNovaMaquina && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovaMaquina}>
              {porCarradas ? "Novo caminhão" : "Nova máquina"}
            </Button>
          </div>
          <div className={locacoesTableWrapClass}>
          <table className="min-w-full text-sm">
            <thead className={locacoesTableHeadClass}>
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">{porCarradas ? "Placa / tipo" : "Horímetro"}</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {maquinas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    {porCarradas
                      ? "Nenhum caminhão vinculado a este contrato."
                      : "Nenhuma máquina vinculada a este contrato."}
                  </td>
                </tr>
              ) : (
                maquinas.map((m) => {
                  const theme = MAQUINARIO_STATUS_THEME[m.status];
                  return (
                    <tr key={m.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 font-medium text-slate-800">{m.nome}</td>
                      <td className="px-4 py-3 text-slate-600">{m.codigoInterno || "—"}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {porCarradas
                          ? [m.placa, m.tipo].filter(Boolean).join(" · ") || "—"
                          : (() => {
                              const h = getHorimetroContratoMaquina(contrato, m.id, registros);
                              return h != null ? `${h} h` : "—";
                            })()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold", theme.badge)}>
                          {MAQUINARIO_STATUS_LABELS[m.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {onHistoricoMaquina && (
                            <Button variant="ghost" size="sm" onClick={() => onHistoricoMaquina(m)} leftIcon={<History className="h-3.5 w-3.5" />}>
                              Histórico
                            </Button>
                          )}
                          {onEditMaquina && (
                            <Button variant="ghost" size="sm" onClick={() => onEditMaquina(m)} leftIcon={<Pencil className="h-3.5 w-3.5" />}>
                              Editar
                            </Button>
                          )}
                          {onDuplicateMaquina && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDuplicateMaquina(m)}
                              leftIcon={<Copy className="h-3.5 w-3.5" />}
                              title="Duplicar"
                            >
                              Duplicar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {detailTab === "manutencoes" && onNovaManutencao && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovaManutencao}>
              Nova manutenção
            </Button>
          </div>
          <div className={locacoesTableWrapClass}>
            <table className="min-w-full text-sm">
              <thead className={locacoesTableHeadClass}>
                <tr>
                  <th className="px-4 py-3">Máquina</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Descrição</th>
                  <th className="px-4 py-3">Prioridade</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {manutencoes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      Nenhuma manutenção para as máquinas deste contrato.
                    </td>
                  </tr>
                ) : (
                  manutencoes.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 font-medium">
                        {resolveMaquinaNome(maquinas, m.maquinaId, m.maquinaNome)}
                      </td>
                      <td className="px-4 py-3">{MANUTENCAO_TIPO_LABELS[m.tipo]}</td>
                      <td className="max-w-xs truncate px-4 py-3 text-slate-600">{m.descricao}</td>
                      <td className="px-4 py-3">{MANUTENCAO_PRIORIDADE_LABELS[m.prioridade]}</td>
                      <td className="px-4 py-3">{MANUTENCAO_STATUS_LABELS[m.status]}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          {m.status !== "finalizada" && (
                            <>
                              {m.status === "aberta" && (
                                <Button size="sm" variant="ghost" loading={busy} onClick={() => void onUpdateManutencaoStatus(m.id, "em_andamento")}>
                                  Iniciar
                                </Button>
                              )}
                              {(m.status === "aberta" || m.status === "em_andamento") && (
                                <Button size="sm" variant="ghost" loading={busy} onClick={() => void onUpdateManutencaoStatus(m.id, "aguardando_peca")}>
                                  Aguard. peça
                                </Button>
                              )}
                              {m.status === "aguardando_peca" && (
                                <Button size="sm" variant="ghost" loading={busy} onClick={() => void onUpdateManutencaoStatus(m.id, "em_andamento")}>
                                  Retomar
                                </Button>
                              )}
                              <Button size="sm" loading={busy} onClick={() => void onUpdateManutencaoStatus(m.id, "finalizada")}>
                                Finalizar
                              </Button>
                            </>
                          )}
                          {onEditManutencao && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onEditManutencao(m)}
                              leftIcon={<Pencil className="h-3.5 w-3.5" />}
                            >
                              Editar
                            </Button>
                          )}
                          {onDeleteManutencao && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeleteManutencao(m)}
                              leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                            >
                              Excluir
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailTab === "relatorios" && (
        <TabRelatorios
          reportPeriod={buildReportPeriodForContrato(contrato)}
          maquinarios={maquinas}
          contratoId={contrato.id}
          dataVersion={dataVersion}
        />
      )}

      {detailTab === "operadores" && !operadorMode && (
        <TabOperadores
          contrato={contrato}
          operadoresCatalog={operadoresCatalog}
          busy={busy}
          onSaveOperadores={onSaveOperadores}
        />
      )}

      {porCarradas && (
        <>
          <ModalReajusteValorCarrada
            open={reajusteOpen}
            contrato={contrato}
            busy={busy}
            onClose={() => setReajusteOpen(false)}
            onSaved={async () => {
              await onRefreshSnapshot?.();
              await loadCarradasContrato();
            }}
          />
          <ModalLeadTimeValorCarrada
            open={leadTimeOpen}
            contratoLabel={`${contrato.codigoContrato} · ${contrato.clienteSiengeNome}`}
            faixas={leadTimeFaixas}
            onClose={() => setLeadTimeOpen(false)}
          />
        </>
      )}
    </div>
  );
}
